import { ref, computed } from 'vue'
import { 
  DEFAULT_AUTH_CONFIG, 
  getSubscriptionKey, 
  getAvailableKeys,
  getAvailableAuthConfigs,
  getUserByEmail,
  type SubscriptionKey, 
  type UserAuth,
  type AuthConfig 
} from '@/config/auth'
import { usePermissions } from './usePermissions'

// Global authentication state
const currentAuthConfig = ref<AuthConfig>(DEFAULT_AUTH_CONFIG)
const authInitialized = ref(false)

export function useAuth() {
  const permissions = usePermissions()

  /**
   * Initialize authentication and load permissions
   */
  async function initialize(): Promise<void> {
    if (authInitialized.value) return
    
    try {
      await permissions.loadPermissions()
      authInitialized.value = true
    } catch (error) {
      console.error('[useAuth] Failed to initialize authentication:', error)
      authInitialized.value = false
    }
  }

  /**
   * Set authentication config (supports both subscription keys and user emails)
   */
  async function setAuthConfig(config: AuthConfig) {
    currentAuthConfig.value = config
    // Reload permissions when auth changes
    await permissions.refreshPermissions()
  }

  /**
   * Set authentication by user email (mimics JWT auth)
   */
  async function setAuthByEmail(email: string) {
    const userConfig = getUserByEmail(email)
    if (userConfig) {
      await setAuthConfig(userConfig)
    }
  }

  /**
   * Set the current subscription key by role
   * @deprecated Use setAuthConfig instead
   */
  async function setAuthRole(role: 'Viewer' | 'Contributor' | 'Administrator') {
    const key = getSubscriptionKey(role)
    const keyConfig = getAvailableKeys().find(k => k.key === key)
    if (keyConfig) {
      await setAuthConfig(keyConfig)
    }
  }

  /**
   * Set the current subscription key by role ID (new RBAC system)
   * @deprecated Use setAuthConfig instead
   */
  async function setAuthRoleById(roleId: number) {
    const keyConfig = getAvailableKeys().find(k => k.roleId === roleId)
    if (keyConfig) {
      await setAuthConfig(keyConfig)
    }
  }

  /**
   * Set the current subscription key directly
   * @deprecated Use setAuthConfig instead
   */
  async function setSubscriptionKey(key: SubscriptionKey) {
    await setAuthConfig(key)
  }

  /**
   * Get the current auth method
   */
  const authMethod = computed(() => currentAuthConfig.value.authMethod)

  /**
   * Get the current subscription key for API requests (if using subscription key auth)
   */
  const subscriptionKey = computed(() => {
    if (currentAuthConfig.value.authMethod === 'subscription-key') {
      return (currentAuthConfig.value as SubscriptionKey).key
    }
    return undefined
  })

  /**
   * Get the current user email (if using user email auth)
   */
  const userEmail = computed(() => {
    if (currentAuthConfig.value.authMethod === 'user-email') {
      return (currentAuthConfig.value as UserAuth).email
    }
    return undefined
  })

  /**
   * Get the current user display name
   */
  const displayName = computed(() => {
    if (currentAuthConfig.value.authMethod === 'user-email') {
      return (currentAuthConfig.value as UserAuth).displayName
    }
    if (currentAuthConfig.value.authMethod === 'subscription-key') {
      return `API Key (${(currentAuthConfig.value as SubscriptionKey).role})`
    }
    return 'Unknown'
  })

  /**
   * Get the current user role (legacy - returns string role name)
   * @deprecated Use roleIds from permissions instead
   */
  const userRole = computed(() => {
    if (currentAuthConfig.value.authMethod === 'subscription-key') {
      return (currentAuthConfig.value as SubscriptionKey).role
    }
    return 'Administrator' // Default for user auth
  })

  /**
   * Get the current user role ID (single role for backward compatibility)
   * @deprecated Use roleIds array instead
   */
  const roleId = computed(() => {
    if (currentAuthConfig.value.authMethod === 'subscription-key') {
      return (currentAuthConfig.value as SubscriptionKey).roleId
    }
    return (currentAuthConfig.value as UserAuth).roleIds[0]
  })

  /**
   * Get the current user role IDs (multi-role support)
   */
  const roleIds = computed(() => {
    if (currentAuthConfig.value.authMethod === 'subscription-key') {
      return [(currentAuthConfig.value as SubscriptionKey).roleId]
    }
    return (currentAuthConfig.value as UserAuth).roleIds
  })

  /**
   * Check if user has contributor access
   * @deprecated Use permissions.hasPermission instead for granular control
   */
  const canEdit = computed(() => {
    if (currentAuthConfig.value.authMethod === 'subscription-key') {
      return ['Contributor', 'Administrator'].includes((currentAuthConfig.value as SubscriptionKey).role)
    }
    return true // User auth has granular permissions
  })

  /**
   * Check if user has viewer access
   * @deprecated Use permissions.canRead instead
   */
  const canView = computed(() => true) // All authenticated users can view

  /**
   * Get available auth configs (both subscription keys and users)
   */
  const availableAuthConfigs = computed(() => getAvailableAuthConfigs())

  /**
   * Get available subscription keys
   * @deprecated Use availableAuthConfigs instead
   */
  const availableKeys = computed(() => getAvailableKeys())

  /**
   * Check if authentication is initialized
   */
  const isInitialized = computed(() => authInitialized.value)

  return {
    // State
    currentAuthConfig: computed(() => currentAuthConfig.value),
    authMethod,
    subscriptionKey,
    userEmail,
    displayName,
    userRole, // deprecated
    roleId, // deprecated
    roleIds,
    availableKeys, // deprecated
    availableAuthConfigs,
    isInitialized,

    // RBAC Integration
    permissions,

    // Legacy Permissions (deprecated)
    canEdit, // deprecated
    canView, // deprecated

    // Actions
    initialize,
    setAuthConfig,
    setAuthByEmail,
    setAuthRole, // deprecated
    setAuthRoleById, // deprecated
    setSubscriptionKey // deprecated
  }
}
